import {
  getStringFromClassNameOrId,
  useDialog,
  usePrevious,
} from '@ecdlink/core';
import {
  ActionModal,
  BannerWrapper,
  DialogPosition,
  TabItem,
  TabList,
} from '@ecdlink/ui';
import format from 'date-fns/format';
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useOnlineStatus } from '@hooks/useOnlineStatus';
import { useAppDispatch } from '@store';
import { analyticsActions } from '@store/analytics';
import {
  ClientDashboardRouteState,
  HcwTabIndex,
  HcwTabNames,
  tabKeyMap,
} from './class-dashboard.types';
import { ClientList } from '../clients-tab/client-list';
import { VisitList } from '../visits-tab/visit-dashboard';
import { HighlightsTab } from '../highlights-tab/highlights-tab';
import { ReactComponent as PollyNeutral } from '@/assets/pollyNeutral.svg';
import { useSelector } from 'react-redux';
import {
  healthCareWorkerActions,
  healthCareWorkerSelectors,
} from '@/store/healthCareWorker';
import { UpdateHealthCareWorkerTabsInputModelInput } from '@ecdlink/graphql';
import { updateHealthCareWorkerTabs } from '@/store/healthCareWorker/healthCareWorker.actions';
import {
  WalkthroughInfoPage,
  WalkthroughInfoPageProps,
} from '@/components/walkthrough/info-page';
import ROUTES from '@/routes/routes';
import { clientSteps } from '../clients-tab/walkthrough/steps';
import { multipleClientsSteps } from '../clients-tab/walkthrough/steps_multiple_clients';
import { clientsDeclineTipSteps } from '../clients-tab/walkthrough/steps_decline_tip';
import { useThunkFetchCall } from '@/hooks/useThunkFetchCall';
import { CaregiverActions } from '@/store/caregiver/caregiver.actions';
import { MotherActions } from '@/store/mother/mother.actions';
import { InfantActions } from '@/store/infant/infant.actions';
import { useWalkthroughContext } from '@/context/walkthrougContext';
import ClientsWrapper from '../clients-tab/walkthrough/clients-wrapper';

export const ClassDashboard: React.FC = () => {
  const [isInfoPage, setIsInfoPage] = useState(false);
  /** 'full' = multi clients tour; 'decline' = tip pointing at the ? help button */
  const [tourMode, setTourMode] = useState<'full' | 'decline'>('full');
  const { setState, state: walkthroughState } = useWalkthroughContext();
  const wasTourRunning = usePrevious(walkthroughState.run);
  const appDispatch = useAppDispatch();
  const { state } = useLocation<ClientDashboardRouteState>();
  const previousActiveTab = usePrevious(state?.activeTabIndex);
  const { isOnline } = useOnlineStatus();
  const dialog = useDialog();
  const history = useHistory();
  const location = useLocation();

  const date = format(new Date(), 'EEEE, d LLLL');

  const { isLoading: isLoadingCaregivers } = useThunkFetchCall(
    'caregivers',
    CaregiverActions.GET_CAREGIVER_CLIENTS
  );
  const { isLoading: isLoadingMothers } = useThunkFetchCall(
    'mothers',
    MotherActions.GET_MOTHERS
  );
  const { isLoading: isLoadingInfants } = useThunkFetchCall(
    'infants',
    InfantActions.GET_INFANTS
  );

  const isLoading = isLoadingCaregivers || isLoadingMothers || isLoadingInfants;

  const healthCareWorker = useSelector(
    healthCareWorkerSelectors?.getHealthCareWorker
  );

  const tabItems: TabItem[] = [
    {
      title: HcwTabNames.Clients,
      initActive: true,
      index: HcwTabIndex.CLIENT,
      child: <ClientList />,
    },
    {
      title: HcwTabNames.Visits,
      initActive: false,
      index: HcwTabIndex.VISIT,
      child: <VisitList />,
    },
    {
      title: HcwTabNames.Highlights,
      initActive: false,
      index: HcwTabIndex.HIGHLIGHTS,
      child: <HighlightsTab />,
    },
  ];

  // Always use multi-client script + dummy list so tours never depend on real caseload size.
  const clientsTourSteps =
    tourMode === 'decline' ? clientsDeclineTipSteps : multipleClientsSteps;
  const clientsTourType =
    tourMode === 'decline' ? ('decline' as const) : ('multi' as const);

  const { infoPageSection, infoPageTitle } = useMemo(
    (): {
      infoPageTitle: string;
      infoPageSection: WalkthroughInfoPageProps['sectionName'];
    } => ({
      infoPageTitle: 'Clients',
      infoPageSection: 'Client folders',
    }),
    []
  );

  /** Minimal tab marker so updateClickedTab can set the clients "seen" flags. */
  const clientsTabMarker = useMemo(
    () =>
      ({
        title: HcwTabNames.Clients,
        initActive: true,
        index: HcwTabIndex.CLIENT,
        child: null,
      } as unknown as TabItem),
    []
  );

  const tourStartTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTourStartTimer = useCallback(() => {
    if (tourStartTimerRef.current) {
      clearInterval(tourStartTimerRef.current);
      tourStartTimerRef.current = null;
    }
  }, []);

  const waitForTargetAndRun = useCallback(
    (targetSelector: string, options?: { tourActive?: boolean }) => {
      clearTourStartTimer();
      const tourActive = options?.tourActive ?? true;
      setState({ run: false, tourActive, stepIndex: 0 });

      const tryStartJoyride = () => {
        if (!document.querySelector(targetSelector)) {
          return false;
        }
        clearTourStartTimer();
        setState({ run: true, tourActive, stepIndex: 0 });
        return true;
      };

      requestAnimationFrame(() => {
        if (tryStartJoyride()) {
          return;
        }

        let attempts = 0;
        const maxAttempts = 40; // ~4s

        tourStartTimerRef.current = setInterval(() => {
          attempts += 1;
          if (tryStartJoyride()) {
            return;
          }
          if (attempts >= maxAttempts) {
            clearTourStartTimer();
            setState({ run: false, tourActive: false, stepIndex: 0 });
            setTourMode('full');
          }
        }, 100);
      });
    },
    [clearTourStartTimer, setState]
  );

  const startClientsTour = useCallback(() => {
    setTourMode('full');
    setIsInfoPage(false);

    // Ensure we're on the clients tab so tour targets exist.
    if ((state?.activeTabIndex ?? HcwTabIndex.CLIENT) !== HcwTabIndex.CLIENT) {
      history.push(location.pathname, { activeTabIndex: HcwTabIndex.CLIENT });
    }

    // Enable tour UI first (multi tour swaps in fake Client type control).
    // Do NOT set run=true yet — ClientList remounts after the info page closes
    // and targets may not exist for a few frames.
    waitForTargetAndRun('#walkthrough-dashboard-client-multi-step-1', {
      tourActive: true,
    });
  }, [history, location.pathname, state?.activeTabIndex, waitForTargetAndRun]);

  /** Design: if user declines, show tip pointing at the ? help button. */
  const startDeclineTip = useCallback(() => {
    setTourMode('decline');
    setIsInfoPage(false);

    if ((state?.activeTabIndex ?? HcwTabIndex.CLIENT) !== HcwTabIndex.CLIENT) {
      history.push(location.pathname, { activeTabIndex: HcwTabIndex.CLIENT });
    }

    // Decline tip only highlights the help button — no dummy list needed.
    waitForTargetAndRun('#walkthrough-clients-help-button', {
      tourActive: false,
    });
  }, [history, location.pathname, state?.activeTabIndex, waitForTargetAndRun]);

  const onHelp = useCallback(() => {
    startClientsTour();
  }, [startClientsTour]);

  const backToDashboard = useCallback(() => {
    if (isInfoPage) {
      return setIsInfoPage(false);
    }

    return history.push('/');
  }, [history, isInfoPage]);

  const updateClickedTab = useCallback(
    (tab?: TabItem) => {
      if (!healthCareWorker) return;

      const prev: UpdateHealthCareWorkerTabsInputModelInput = {
        clickedDashboardClientsTab:
          healthCareWorker.clickedDashboardClientsTab ?? false,
        clickedDashboardClientsTabB:
          healthCareWorker.clickedDashboardClientsTabB ?? false,
        clickedDashboardVisitsTab:
          healthCareWorker.clickedDashboardVisitsTab ?? false,
        clickedDashboardHighlightsTab:
          healthCareWorker.clickedDashboardHighlightsTab ?? false,
      };

      let next: typeof prev = { ...prev };

      if (
        tab?.title === HcwTabNames.Clients &&
        !next.clickedDashboardClientsTab
      ) {
        next.clickedDashboardClientsTab = true;
      }

      // Keep B in sync so legacy environments don't re-prompt multi tour.
      if (
        tab?.title === HcwTabNames.Clients &&
        !next.clickedDashboardClientsTabB
      ) {
        next.clickedDashboardClientsTabB = true;
      }

      if (
        tab?.title === HcwTabNames.Visits &&
        !next.clickedDashboardVisitsTab
      ) {
        next.clickedDashboardVisitsTab = true;
      }

      if (
        tab?.title === HcwTabNames.Highlights &&
        !next.clickedDashboardHighlightsTab
      ) {
        next.clickedDashboardHighlightsTab = true;
      }

      //  Detect ONLY false → true changes
      const changedKeys = (Object.keys(next) as (keyof typeof next)[]).filter(
        (key) => prev[key] === false && next[key] === true
      );

      // Abort if nothing changed
      if (!changedKeys.length) return;

      // update the state
      changedKeys.forEach((key) => {
        const mappedTab = tabKeyMap[key];
        appDispatch(
          healthCareWorkerActions.setHCWTabClicked({
            tab: mappedTab,
            clicked: next[key]!,
            isOnline: isOnline,
          })
        );
      });

      // Send ONLY changed fields to backend
      if (!isOnline) return;

      const input = changedKeys.reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {} as Partial<typeof next>);

      // update database
      appDispatch(
        updateHealthCareWorkerTabs({
          input: input,
          userId: healthCareWorker?.id!,
        })
      );
    },
    [appDispatch, healthCareWorker, isOnline]
  );

  const handleWelcomeDialogA = useCallback(() => {
    const currentTab = state?.activeTabIndex ?? 0;
    // Dummy data means the tour no longer requires real clients on the device.
    const showDialog = !healthCareWorker?.clickedDashboardClientsTab;

    if (currentTab === HcwTabIndex.CLIENT && showDialog) {
      return dialog({
        position: DialogPosition.Middle,
        color: 'bg-white',
        render(onClose) {
          return (
            <ActionModal
              customIcon={<PollyNeutral className="mb-3 h-24 w-24" />}
              title={`Hello!`}
              detailText="Would you like me to show you how to use the client folders section?"
              actionButtons={[
                {
                  colour: 'primary',
                  text: 'Yes, help me!',
                  textColour: 'white',
                  type: 'filled',
                  leadingIcon: 'CheckCircleIcon',
                  onClick: () => {
                    // Must pass clients tab so "seen" flags actually update.
                    updateClickedTab(clientsTabMarker);
                    startClientsTour();
                    onClose();
                  },
                },
                {
                  colour: 'primary',
                  text: 'No, skip',
                  textColour: 'primary',
                  type: 'outlined',
                  leadingIcon: 'ClockIcon',
                  onClick: () => {
                    updateClickedTab(clientsTabMarker);
                    onClose();
                    // Design: after decline, point users at the ? help control
                    startDeclineTip();
                  },
                },
              ]}
            />
          );
        },
      });
    }
  }, [
    clientsTabMarker,
    dialog,
    healthCareWorker?.clickedDashboardClientsTab,
    startClientsTour,
    startDeclineTip,
    state?.activeTabIndex,
    updateClickedTab,
  ]);

  useEffect(() => {
    if (!isOnline) {
      appDispatch(
        analyticsActions.createViewTracking({
          pageView: window.location.pathname,
          title: 'Clients-Dashboard',
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline]);

  const goBack = useCallback(() => {
    if (isInfoPage) {
      return setIsInfoPage(false);
    }

    return history.push(ROUTES.CLIENTS.ROOT);
  }, [history, isInfoPage]);

  useEffect(() => {
    if (previousActiveTab !== state?.activeTabIndex) {
      handleWelcomeDialogA();
    }
  }, [handleWelcomeDialogA, previousActiveTab, state?.activeTabIndex]);

  // Reset decline mode when any clients Joyride finishes so the next help starts the full tour.
  useEffect(() => {
    if (wasTourRunning && !walkthroughState.run) {
      setTourMode('full');
    }
  }, [wasTourRunning, walkthroughState.run]);

  // End tour cleanly if the user leaves the clients dashboard mid-tour.
  useEffect(() => {
    return () => {
      clearTourStartTimer();
      setState({ run: false, stepIndex: 0, tourActive: false });
      setTourMode('full');
    };
  }, [clearTourStartTimer, setState]);

  return (
    <>
      <ClientsWrapper steps={clientsTourSteps} type={clientsTourType} />
      <BannerWrapper
        showBackground={false}
        size="medium"
        renderBorder={true}
        title={infoPageTitle}
        subTitle={date}
        color={'primary'}
        onBack={() => backToDashboard()}
        displayHelp={
          !isInfoPage &&
          (state?.activeTabIndex ?? HcwTabIndex.CLIENT) ===
            HcwTabIndex.CLIENT &&
          !isLoading
        }
        helpId="walkthrough-clients-help-button"
        onHelp={() => setIsInfoPage(true)}
        displayOffline={!isOnline}
      >
        {isInfoPage ? (
          <WalkthroughInfoPage
            sectionName={infoPageSection}
            onHelp={onHelp}
            onClose={goBack}
          />
        ) : (
          <TabList
            id={getStringFromClassNameOrId(clientSteps[0].target)}
            className="bg-uiBg"
            tabItems={tabItems}
            setSelectedIndex={state?.activeTabIndex ?? 0}
            tabSelected={(tab) => {
              updateClickedTab(tab);
              history.push(location.pathname, { activeTabIndex: tab.index });
            }}
          />
        )}
        <div id="walkthrough-last-step" className="w-full"></div>
      </BannerWrapper>
    </>
  );
};

export default ClassDashboard;
